// withFileUpload.js - Higher Order Component for file uploads
import React, { useState } from 'react';
import axios from 'axios';
import { handleApiError } from '../Common/APIUtils';
import { UPLOAD_URL } from "../../imports";

const withFileUpload = (WrappedComponent) => {
  return (props) => {
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);

    /**
     * Upload file to the backend /upload-audio endpoint
     * @param {File} file - The file to upload
     * @param {string} projectCode - Project code
     * @param {string} clientId - Client ID
     * @returns {Promise} - Returns promise with response data
     */
    const uploadFile = async (file, projectCode, clientId) => {
      const accessToken = localStorage.getItem('access_token');
      const resourceGroup = localStorage.getItem('resource_group');
      console.log('🔍 Upload credentials:', { accessToken, resourceGroup, projectCode, clientId });
      const formData = new FormData();
      formData.append('file', file);
      formData.append('project_code', projectCode);
      formData.append('client_id', clientId);

      try {
        setIsUploading(true);
        setUploadProgress(0);

        const response = await axios.post(
          `${UPLOAD_URL}/upload-audio`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
              Authorization: `Bearer ${localStorage.getItem('access_token')}`,
              'x-resource-group': localStorage.getItem('resource_group')
            },
            onUploadProgress: (progressEvent) => {
              const percentCompleted = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
              setUploadProgress(percentCompleted);
            },
          }
        );

        return {
          success: true,
          data: response.data,
        };
      } catch (error) {
        const errorMessage = handleApiError(error);
        return {
          success: false,
          error: errorMessage,
        };
      } finally {
        setIsUploading(false);
        setUploadProgress(0);
      }
    };

    /**
     * Upload multiple files sequentially
     * @param {File[]} files - Array of files to upload
     * @param {string} projectCode - Project code
     * @param {string} clientId - Client ID
     * @returns {Promise} - Returns promise with all results
     */
    const uploadMultipleFiles = async (files, projectCode, clientId) => {
      const results = [];
      
      for (let i = 0; i < files.length; i++) {
        const result = await uploadFile(files[i], projectCode, clientId);
        results.push({
          fileName: files[i].name,
          ...result,
        });
      }

      return results;
    };

    return (
      <WrappedComponent
        {...props}
        uploadFile={uploadFile}
        uploadMultipleFiles={uploadMultipleFiles}
        isUploading={isUploading}
        uploadProgress={uploadProgress}
      />
    );
  };
};

export default withFileUpload;